import { Navigate } from 'react-router-dom';
import { useCatalogs } from '../../api/hooks';
import { useActiveCatalog } from '../../hooks/useActiveCatalog';
import { Spinner } from '../ui/Spinner';
import { Btn } from '../ui/Btn';
import { darkTheme, FONT } from '../../theme/tokens';

const t = darkTheme;

/**
 * The app index (`/`). Sends a signed-in user straight into their catalog
 * workspace — the entities table — rather than a standalone landing (SC-2716).
 * The old agent chat-briefing home (`AgentTab`) is parked for the future catalog
 * Home/Overview view (SC-2717), so `/` is no longer a destination.
 *
 * Routes to `/welcome` when the user has no catalog yet. On a catalogs-fetch
 * error it holds here with a retry rather than bouncing to `/welcome` (which
 * reads the same list and would loop) or a workspace URL we can't form without
 * an id.
 */
export function HomeRoute() {
  const { active, isLoading } = useActiveCatalog();
  const { isError, refetch } = useCatalogs();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size={32} />
      </div>
    );
  }

  // A resolved catalog → straight into its workspace (the entities table).
  if (active) return <Navigate to={`/catalogs/${active.id}/entities`} replace />;

  // No catalog yet (and the list didn't error) → onboarding.
  if (!isError) return <Navigate to="/welcome" replace />;

  // The catalog list failed to load. Don't redirect to /welcome (it reads the
  // same list and would bounce right back — a loop) or into a workspace (no id
  // to route to). Hold here with a retry.
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        fontFamily: FONT,
        color: t.text3,
        background: t.bg,
      }}
    >
      <div style={{ fontSize: 14 }}>Couldn’t load your catalogs.</div>
      <Btn onClick={() => void refetch()} t={t}>
        Try again
      </Btn>
    </div>
  );
}
